import React, { useState } from 'react';
import api from '../api/axios';
import '../pages/Pages.css';

interface ReceiptUploadUrlResponse {
  fileUrl: string;
  uploadUrl: string;
  fileKey: string;
}

interface AddPointsProps {
  clientId: string;
  onSuccess: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
}

type TransactionSource = 'LojaFisica' | 'Telefone' | 'Ifood' | 'Delivery' | 'Resgate';

interface CreateManualTransactionDto {
  clientId: string;
  restaurantId: string;
  points?: number;
  amount?: number;
  transactionDate: string;
  source?: TransactionSource;
  receiptPhotoUrl?: string;
  notes?: string;
  isDebit: boolean;
}

const AddPoints: React.FC<AddPointsProps> = ({ clientId, onSuccess, onClose, showCloseButton = false }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const [pointsInput, setPointsInput] = useState<string>('');
  const [amountInput, setAmountInput] = useState<string>('');
  
  const [formData, setFormData] = useState<CreateManualTransactionDto>({
    clientId: clientId,
    restaurantId: '',
    transactionDate: new Date().toISOString().split('T')[0],
    source: 'LojaFisica',
    receiptPhotoUrl: '',
    notes: '',
    isDebit: false
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadReceipt = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    try {
      setUploading(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);

      const response = await api.post<ReceiptUploadUrlResponse>('/client/receipts/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.fileUrl;
    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Erro ao fazer upload da foto');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Validação baseada no tipo (crédito ou débito)
      if (formData.isDebit) {
        // Débito: validar pontos
        const pointsValue = parseInt(pointsInput) || 0;
        if (pointsValue <= 0) {
          alert('Por favor, informe a quantidade de pontos a debitar (deve ser maior que zero)');
          setLoading(false);
          return;
        }
      } else {
        // Crédito: validar valor
        const amountValue = parseFloat(amountInput) || 0;
        if (amountValue <= 0) {
          alert('Por favor, informe o valor da conta (deve ser maior que zero)');
          setLoading(false);
          return;
        }
        if (!formData.source) {
          alert('Por favor, selecione a origem da transação');
          setLoading(false);
          return;
        }
      }

      // Upload photo if selected
      let photoUrl: string | undefined;
      if (selectedFile) {
        photoUrl = await uploadReceipt() || undefined;
      }

      const payload: CreateManualTransactionDto = {
        clientId: clientId,
        restaurantId: '',
        points: formData.isDebit ? parseInt(pointsInput) || 0 : undefined,
        amount: !formData.isDebit ? parseFloat(amountInput) || 0 : undefined,
        transactionDate: formData.transactionDate,
        source: formData.isDebit ? undefined : formData.source,
        receiptPhotoUrl: photoUrl,
        notes: formData.notes,
        isDebit: formData.isDebit
      };

      await api.post('/admin/transactions/manual', payload);
      
      alert('Transação cadastrada com sucesso!');
      onSuccess();
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating transaction:', error);
      const message = error.response?.data?.message || 'Erro ao cadastrar transação';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="add-points-form">
      <div className="form-row">
        <div className="form-group">
          <label>Data da Transação *</label>
          <input
            type="date"
            required
            value={formData.transactionDate}
            onChange={(e) => setFormData({...formData, transactionDate: e.target.value})}
          />
        </div>

        {!formData.isDebit && (
          <div className="form-group">
            <label>Origem *</label>
            <select
              required
              value={formData.source || 'LojaFisica'}
              onChange={(e) => setFormData({...formData, source: e.target.value as TransactionSource})}
            >
              <option value="LojaFisica">Loja Física</option>
              <option value="Telefone">Telefone</option>
              <option value="Ifood">iFood</option>
              <option value="Delivery">Delivery</option>
            </select>
          </div>
        )}
      </div>

      {formData.isDebit ? (
        // Modo Débito: Pontos diretos
        <div className="form-group">
          <label>
            Pontos a Debitar *
            <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px', fontWeight: 'normal' }}>
              ⚠️ Pontos serão DESCONTADOS do cliente (valor negativo)
            </span>
          </label>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{
              position: 'absolute',
              left: '16px',
              fontSize: '20px',
              fontWeight: 700,
              color: '#DC2626',
              zIndex: 1,
              pointerEvents: 'none'
            }}>
              -
            </span>
            <input
              type="number"
              required
              min="1"
              step="1"
              value={pointsInput}
              onChange={(e) => {
                const value = e.target.value;
                setPointsInput(value);
              }}
              placeholder=""
              style={{ paddingLeft: '40px' }}
            />
          </div>
          <div className="info-box" style={{ marginTop: '8px' }}>
            💡 Informe a quantidade de pontos que serão descontados do cliente.
          </div>
        </div>
      ) : (
        // Modo Crédito: Valor decimal
        <div className="form-group">
          <label>
            Valor da Conta (R$) *
            <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px', fontWeight: 'normal' }}>
              ✓ Pontos serão calculados automaticamente baseado no valor
            </span>
          </label>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{
              position: 'absolute',
              left: '16px',
              fontSize: '20px',
              fontWeight: 700,
              color: '#2563EB',
              zIndex: 1,
              pointerEvents: 'none'
            }}>
              +
            </span>
            <input
              type="number"
              required
              min="0.01"
              step="0.01"
              value={amountInput}
              onChange={(e) => {
                const value = e.target.value;
                setAmountInput(value);
              }}
              placeholder="0.00"
              style={{ paddingLeft: '40px' }}
            />
          </div>
          <div className="info-box" style={{ marginTop: '8px' }}>
            💡 Informe o valor da conta. Os pontos serão calculados automaticamente baseado no plano de fidelidade do cliente.
          </div>
        </div>
      )}

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={formData.isDebit}
            onChange={(e) => {
              const isDebit = e.target.checked;
              setFormData({
                ...formData, 
                isDebit,
                source: isDebit ? undefined : (formData.source || 'LojaFisica')
              });
              // Reset inputs when switching between credit/debit
              setPointsInput('');
              setAmountInput('');
            }}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span>Debitar pontos do cliente (valor negativo)</span>
        </label>
      </div>

      <div className="form-group">
        <label>Foto da Nota Fiscal (Opcional)</label>
        <div className="file-input">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
          />
          {selectedFile && <span className="file-name">📷 {selectedFile.name}</span>}
        </div>
      </div>

      <div className="form-group">
        <label>Observações (Opcional)</label>
        <textarea
          rows={3}
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Observações sobre a transação..."
        />
      </div>

      <div className="modal-actions">
        {showCloseButton && onClose && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading || uploading}
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={loading || uploading}
        >
          {loading || uploading ? 'Processando...' : '✓ Cadastrar Transação'}
        </button>
      </div>
    </form>
  );
};

export default AddPoints;

